import { countCodePoints } from "@liveswitch-contact/types";

// Character counting units.
//
// APP-WIDE DEFAULT IS "codepoints" AND ADOPTERS SHOULD NOT OVERRIDE IT. Every adopted textarea
// counts the same way, so one emoji reports the same number in every field — per-field units were
// tried and rejected because the same character counted 1 in one box and 2 in another, which is the
// inconsistency the shared component exists to remove.
//
// Code points is also the unit the storage actually enforces: Postgres VARCHAR(n) counts code
// points (Conversations.notes is varchar(2048), Snapshots.notes varchar(1400)), and the conversation
// notes endpoint validates Array.from(notes).length. Counting graphemes instead would be LOOSER than
// the column — 2048 grapheme clusters of a variation-selector emoji is 4096 code points — so an
// all-emoji field would pass the counter and then fail the save.
//
// graphemes counts visible clusters (Intl.Segmenter); utf16 counts JS .length code units. Both stay
// available for genuine exceptions, but an override is a deliberate, documented choice — and where a
// server counts a different unit, prefer an invisible extra rule in THAT unit over changing the
// visible counter (see ActionSchedule/ActionVideo's notificationTemplateServerLimitRules).
export type CharacterCountUnit = "codepoints" | "graphemes" | "utf16";

// Constructed lazily, not at module scope: `Intl.Segmenter` is absent on older engines (Firefox
// only shipped it in 125), and a top-level `new Intl.Segmenter(...)` would throw during *import* —
// taking down every consumer of this module, including the code-point counters that never need a
// segmenter. Resolved once and memoized; `null` means "unavailable, use the fallback below".
// break-it round 1
// Structural type rather than `Intl.Segmenter`: the global type only exists under lib es2022.intl+,
// and this module is compiled by several packages with differing lib settings. We feature-detect at
// runtime regardless, so only the shape we actually use is worth naming.
type GraphemeSegmenter = { segment(value: string): Iterable<unknown> };
type SegmenterConstructor = new (locales: undefined, options: { granularity: "grapheme" }) => GraphemeSegmenter;

let graphemeSegmenter: GraphemeSegmenter | null | undefined;
let warnedAboutMissingSegmenter = false;

function resolveGraphemeSegmenter(): GraphemeSegmenter | null {
	if (graphemeSegmenter === undefined) {
		try {
			const segmenterConstructor = (Intl as unknown as { Segmenter?: SegmenterConstructor })?.Segmenter;
			graphemeSegmenter =
				typeof segmenterConstructor === "function" ? new segmenterConstructor(undefined, { granularity: "grapheme" }) : null;
		} catch {
			// A present-but-unusable implementation is treated the same as an absent one.
			graphemeSegmenter = null;
		}
	}
	return graphemeSegmenter;
}

// Permanently drop a segmenter that proved unreliable, so every later call takes the code-point
// fallback instead of re-trusting it. Caching it and re-calling would let a partial result (e.g. one
// segment for a two-character string) under-count, which is the one thing the fallback exists to
// prevent. break-it round 3
function retireGraphemeSegmenter(): void {
	graphemeSegmenter = null;
}

export { countCodePoints };

export function countGraphemes(value: string): number {
	if (!value) return 0;
	const segmenter = resolveGraphemeSegmenter();
	if (segmenter) {
		// Guard the USE as well as the construction: a present-but-broken implementation can throw from
		// segment(), or yield nothing for non-empty input. Either would break the never-under-count
		// guarantee below, so both fall through to the code-point path. break-it round 2
		//
		// SCOPE OF THE GUARANTEE (stated precisely, because adversarial rounds kept probing its edge):
		// we detect a segmenter that is ABSENT, THROWS, or does not COVER its input, and fall back. We do
		// NOT detect one that covers the input but reports the wrong boundaries — e.g. claiming "abc👍" is
		// a single grapheme. Catching that would require reimplementing UAX #29 segmentation, i.e. doing
		// the segmenter's job to check the segmenter, which is the wrong trade for a display counter. Any
		// conforming Intl.Segmenter is trusted once it covers the input. break-it round 5
		try {
			// Count segments AND verify they actually cover the input. A positive-but-incomplete result
			// (e.g. one segment for "ab👍") is the dangerous case: it looks valid and silently
			// under-counts, which is the one thing this fallback exists to prevent. Real Segmenter records
			// always carry the matched `segment` string, so a conforming implementation validates cleanly;
			// anything that yields opaque records or partial coverage is treated as broken.
			// break-it round 4
			let count = 0;
			let reconstructed = "";
			let recordsAreOpaque = false;
			for (const segmentRecord of segmenter.segment(value)) {
				count++;
				const segmentText = (segmentRecord as { segment?: unknown })?.segment;
				if (typeof segmentText !== "string") {
					recordsAreOpaque = true;
					break;
				}
				// Compared by CONTENT, not just total length: two records of "xx" sum to the right length
				// for "abcd" while covering none of it. break-it round 5
				reconstructed += segmentText;
			}
			if (count > 0 && !recordsAreOpaque && reconstructed === value) return count;
			// Zero segments, opaque records, or coverage that doesn't reconstruct the input all mean this
			// implementation cannot be trusted for any input — retire it so later calls don't get a
			// plausible-but-wrong partial count from it. break-it round 3
			retireGraphemeSegmenter();
		} catch {
			// Same reasoning: a segmenter that has thrown once is not trustworthy for subsequent values.
			// break-it round 3
			retireGraphemeSegmenter();
		}
	}
	// Degrade to code points rather than throwing. Code points >= graphemes, so the counter reads
	// higher than ideal for emoji (the pre-LSP-17265 behaviour) but never under-counts — it can't
	// let a value through that a grapheme count would have rejected.
	if (!warnedAboutMissingSegmenter) {
		warnedAboutMissingSegmenter = true;
		console.warn("[characterCount] Intl.Segmenter unusable — counting graphemes as code points for this session.");
	}
	return countCodePoints(value);
}

export function countUtf16(value: string): number {
	return (value ?? "").length;
}

export function counterFor(unit: CharacterCountUnit): (value: string) => number {
	switch (unit) {
		case "codepoints":
			return countCodePoints;
		case "graphemes":
			return countGraphemes;
		case "utf16":
			return countUtf16;
		default: {
			// COMPILE TIME: adding a CharacterCountUnit without a case here is still a hard error — the
			// `never` assignment fails to compile (LSP-17265 review, Finding F).
			const unhandledUnit: never = unit;

			// RUNTIME: warn and degrade rather than throw. An earlier round of this PR threw here, on the
			// reasoning that a silent fallback could miscount. That was the wrong call for the actual call
			// sites: `counter-unit` is only a compile-time union, ActionSchedule.vue / ActionVideo.vue are
			// plain Options-API `<script>` blocks with no `lang="ts"`, and contact-admin runs no vue-tsc /
			// type-check step in CI — so a typo'd literal ships clean and then throws inside a `computed`
			// during render, blanking the whole dialog. A wrong-by-one counter is a far cheaper failure
			// than an unusable form, and throwing was inconsistent with the warn-and-degrade approach this
			// same file uses for a missing Segmenter and AppTextarea uses for a stray maxlength.
			//
			// Degrades to countUtf16 specifically because it is the STRICTEST of the three (utf16 >=
			// codepoints >= graphemes for any input), so a mis-specified unit can never under-count and
			// let a value through that the intended unit would have rejected.
			console.error(
				`[characterCount] Unknown counter unit ${JSON.stringify(unhandledUnit)} — falling back to UTF-16 code units. ` +
					`Valid units: "codepoints" | "graphemes" | "utf16".`
			);
			return countUtf16;
		}
	}
}
